import { GenerationStyleTabs } from 'core/constants/enums/GenerationStyleTabs.ts';
import { RendernetErrors } from 'core/constants/enums/RendernetErrors.ts';
import { ToastType } from 'core/constants/enums/ToastTypeEnum.ts';
import { sendToast } from 'core/layouts/components/toast/ToastUtils';
import {
	getUserProfileAsync,
	musicVideoCreateSceneModalSelector,
	setBalance,
	toggleMusicVideoCreateSceneModal,
} from 'core/store/reducers/appReducer/AppSlice';
import { updateCanvasFeedItems } from 'core/store/reducers/canvasReducer/canvasDataMappingUtils';
import {
	canvasFeedDataMapSelector,
	canvasFeedMapSelector,
	canvasFluxModelsSelector,
	canvasIdSelector,
	canvasLightningModelsSelector,
	canvasPromptInputSelector,
	selectedLorasSelector,
	setCanvasControlNetImage,
	setCanvasFacelockImage,
	setCanvasFeedMap,
	setCanvasPromptInput,
	setCanvasSelectedFluxModels,
	setCanvasSelectedLightningModels,
	setCanvasSelectedModels,
	setCanvasSelectedStyles,
	setCanvasSettings,
	setCurrentGenerationStyleTab,
	setMoveStageToLatestGeneration,
	setSelectedCanvasMediaThumbnail,
	setSelectedLoras,
	updateCanvasPendingUpdateMediaList,
	uploadCanvasControlNetImageLinkAsync,
	uploadCanvasFaceLockImageLinkAsync,
} from 'core/store/reducers/canvasReducer/CanvasSlice';
import {
	characterGenerationDataSelector,
	clearSelectedCharacter,
	listCharactersSelector,
	setCharacterGenerationData,
	setSelectedCharacter,
} from 'core/store/reducers/characterStudioReducer/CharaterStudioSlice';
import { base64Prefix } from 'core/utils/stringUtils';
import { getErrorImgForAspectRatio } from 'core/utils/utils';

export const handleSetCanvasSelectedModel =
	({ models, model, replace }) =>
	(dispatch) => {
		dispatch(setCanvasSelectedModels({ models, model, replace }));
	};

export const handleSetCanvasSelectedStyle =
	({ styles, style, replace }) =>
	(dispatch) => {
		dispatch(setCanvasSelectedStyles({ styles, style, replace }));
	};

export const handleSetCanvasSelectedLightningModel = (lightningModels) => (dispatch) => {
	dispatch(
		setCanvasSelectedLightningModels(
			Array.isArray(lightningModels) ? [...lightningModels] : lightningModels,
		),
	);
};

export const handleSetCanvasSelectedFluxModel = (fluxModels) => (dispatch) => {
	dispatch(setCanvasSelectedFluxModels(Array.isArray(fluxModels) ? [...fluxModels] : fluxModels));
};

export const handleAddCanvasSelectedLora = (lora) => (dispatch, getState) => {
	const promptInput = canvasPromptInputSelector(getState());
	const newPromptInput = `${promptInput} ${lora.trigger}`;
	dispatch(setCanvasPromptInput(newPromptInput));
};

export const handleRemoveCanvasSelectedLora = (loraId) => (dispatch, getState) => {
	const selectedLoras = selectedLorasSelector(getState());

	const newSelectedLoras = selectedLoras.filter((lor) => lor.id !== loraId);

	dispatch(setSelectedLoras(newSelectedLoras));
};

export const handleUpdateCanvasSelectedLoraEffect = (loraId, value) => (dispatch, getState) => {
	const selectedLoras = selectedLorasSelector(getState());

	const newSelectedLoras = selectedLoras.map((lor) => {
		if (lor.id === loraId) {
			return {
				...lor,
				effect: value,
			};
		}
		return lor;
	});

	dispatch(setSelectedLoras(newSelectedLoras));
};

export const handleReuseControlNetAndFacelock = (settings) => (dispatch) => {
	if (settings?.attributes) {
		const { controlNet, facelock } = settings.attributes;
		const linkPrefix = 'https:/';

		if (controlNet && controlNet?.inputImage) {
			if (controlNet?.inputImage.substring(0, linkPrefix.length) === linkPrefix) {
				dispatch(uploadCanvasControlNetImageLinkAsync(controlNet?.inputImage));
			} else {
				dispatch(setCanvasControlNetImage(`${base64Prefix}${controlNet?.inputImage}`));
			}
		}
		if (facelock && facelock?.inputImage) {
			if (facelock?.inputImage.substring(0, linkPrefix.length) === linkPrefix) {
				dispatch(uploadCanvasFaceLockImageLinkAsync(facelock?.inputImage));
			} else {
				dispatch(setCanvasFacelockImage(`${base64Prefix}${facelock?.inputImage}`));
			}
		}
	}
};

export const handelCanvasImagesErrorFromSocket = (generationDetails) => (dispatch, getState) => {
	const studioFeedDataMap = canvasFeedDataMapSelector(getState());
	const studioFeedMap = canvasFeedMapSelector(getState());
	const canvasId = canvasIdSelector(getState());
	const studioFeedMapCopy = {};

	const { images, credits } = generationDetails;

	Object.keys(images).map((imageId) => {
		if (Object.hasOwn(studioFeedMap, imageId)) {
			const { aspectRatio } = studioFeedMap[imageId];

			studioFeedMapCopy[imageId] = {
				...studioFeedMap[imageId],
				loading: false,
				status: 'failed',

				src: getErrorImgForAspectRatio(aspectRatio),
				thumbnail: getErrorImgForAspectRatio(aspectRatio),
				canvasSettings: {
					...studioFeedMap[imageId].canvasSettings,
					image: getErrorImgForAspectRatio(aspectRatio),
				},
			};
		}

		return null;
	});

	dispatch(setBalance(credits));
	dispatch(
		setCanvasFeedMap(
			updateCanvasFeedItems(studioFeedDataMap, canvasId, 'media', studioFeedMapCopy),
		),
	);
	dispatch(updateCanvasPendingUpdateMediaList(Object.values(studioFeedMapCopy ?? {})));
};

export const handelCanvasMediaErrorFromSocket = (generationDetails) => (dispatch, getState) => {
	const studioFeedDataMap = canvasFeedDataMapSelector(getState());
	const studioFeedMap = canvasFeedMapSelector(getState());
	const canvasId = canvasIdSelector(getState());
	const studioFeedMapCopy = {};
	const musicVideoScenesModalData = musicVideoCreateSceneModalSelector(getState());

	const { media, credits, error_code: errorCode } = generationDetails;
	if (errorCode === RendernetErrors.CONTENT_MODERATION) {
		sendToast(ToastType.GEN_FAILED_DUE_TO_CONTENT_MODERATION);
	}
	if (errorCode === RendernetErrors.VIDEO_ANYONE_FAL_GENERATIONS) {
		sendToast(ToastType.VIDEO_GENERATION_FAILED);
	}
	if (errorCode === RendernetErrors.BLOCK_USER_DUE_TO_CONTENT_MODERATION) {
		dispatch(getUserProfileAsync());
	}
	const [firstMediaId] = Object.keys(media);
	if (firstMediaId === musicVideoScenesModalData.mediaId) {
		dispatch(toggleMusicVideoCreateSceneModal({ open: false, mediaId: '' }));
	}
	Object.keys(media).map((mediaId) => {
		if (Object.hasOwn(studioFeedMap, mediaId)) {
			studioFeedMapCopy[mediaId] = {
				...studioFeedMap[mediaId],
				loading: false,
				status: 'failed',

				src: media[mediaId].link ? media[mediaId].link : getErrorImgForAspectRatio(1),
				thumbnail: media[mediaId].link ? media[mediaId].link : getErrorImgForAspectRatio(1),
				canvasSettings: {
					...studioFeedMap[mediaId].canvasSettings,
					image: media[mediaId].link ? media[mediaId].link : getErrorImgForAspectRatio(1),
				},
			};
		}

		return null;
	});

	dispatch(setBalance(credits));
	dispatch(
		setCanvasFeedMap(
			updateCanvasFeedItems(studioFeedDataMap, canvasId, 'media', studioFeedMapCopy),
		),
	);
	dispatch(updateCanvasPendingUpdateMediaList(Object.values(studioFeedMapCopy ?? {})));
};

export const loadCanvasFeedImageFromSocket = (generationDetails) => (dispatch, getState) => {
	const studioFeedDataMap = canvasFeedDataMapSelector(getState());
	const studioFeedMap = canvasFeedMapSelector(getState());
	const canvasId = canvasIdSelector(getState());
	const studioFeedMapCopy = {};
	const { success: generationSuccess, images: generatedImages, credits } = generationDetails;

	if (generationSuccess) {
		Object.keys(generatedImages).map((imageId) => {
			if (Object.hasOwn(studioFeedMap, imageId)) {
				studioFeedMapCopy[imageId] = {
					...studioFeedMap[imageId],
					loading: false,
					status: 'success',
					isNsfw: generatedImages[imageId].is_nsfw,

					src: generatedImages[imageId].image,
					thumbnail: generatedImages[imageId].thumbnail,
					canvasSettings: {
						...studioFeedMap[imageId]?.canvasSettings,
						image: generatedImages[imageId].image,
						seed: generatedImages[imageId].seed,
					},
				};
			}

			return null;
		});

		dispatch(
			setCanvasFeedMap(
				updateCanvasFeedItems(studioFeedDataMap, canvasId, 'media', studioFeedMapCopy),
			),
		);
		dispatch(updateCanvasPendingUpdateMediaList(Object.values(studioFeedMapCopy ?? {})));
	} else {
		dispatch(handelCanvasImagesErrorFromSocket(generationDetails));
	}
	dispatch(setBalance(credits));
};
export const loadCharacterMediaFromSocket = (generationDetails) => (dispatch, getState) => {
	const { success: generationSuccess, media: generatedMedia, credits } = generationDetails;
	const generatedMediaData = characterGenerationDataSelector(getState());

	const media = generatedMedia[generatedMediaData.mediaId];
	if (generationSuccess && media != null) {
		dispatch(
			setCharacterGenerationData({
				...generatedMediaData,
				mediaUrl: media.link,
				thumbnail: media.thumbnail,
				loading: false,
				status: 'success',
				isNsfw: media.is_nsfw,
			}),
		);
	}
	dispatch(setBalance(credits));
};

export const loadCharacterMediaErrorFromSocket = (generationDetails) => (dispatch, getState) => {
	const { success: generationSuccess, media: generatedMedia, credits } = generationDetails;
	const generatedMediaData = characterGenerationDataSelector(getState());

	const media = generatedMedia[generatedMediaData.mediaId] ?? {};
	if (!generationSuccess) {
		dispatch(
			setCharacterGenerationData({
				...generatedMediaData,
				mediaUrl: null,
				thumbnail: null,
				loading: false,
				status: 'failed',
				isNsfw: media.is_nsfw,
			}),
		);
	}
	sendToast(ToastType.DEFAULT_SOMETHING_WENT_WRONG_TRY_AGAIN);
	dispatch(setBalance(credits));
};
export const loadCanvasFeedMediaFromSocket = (generationDetails) => (dispatch, getState) => {
	const studioFeedDataMap = canvasFeedDataMapSelector(getState());
	const studioFeedMap = canvasFeedMapSelector(getState());
	const canvasId = canvasIdSelector(getState());
	const studioFeedMapCopy = {};

	const { success: generationSuccess, media: generatedMedia, credits } = generationDetails;

	if (generationSuccess) {
		Object.keys(generatedMedia).map((mediaId) => {
			if (Object.hasOwn(studioFeedMap, mediaId)) {
				studioFeedMapCopy[mediaId] = {
					...studioFeedMap[mediaId],
					loading: false,
					status: 'success',
					isNsfw: generatedMedia[mediaId].is_nsfw,

					src: generatedMedia[mediaId].link,
					thumbnail: generatedMedia[mediaId].thumbnail,
					canvasSettings: {
						...studioFeedMap[mediaId]?.canvasSettings,
						image: generatedMedia[mediaId].link,
						seed: generatedMedia[mediaId].seed,
						attributes: {
							...studioFeedMap[mediaId]?.canvasSettings?.attributes,
							faceSwap: studioFeedMap[mediaId]?.canvasSettings?.attributes?.faceSwap && {
								...studioFeedMap[mediaId]?.canvasSettings?.attributes?.faceSwap,
								metadata: generatedMedia[mediaId].metadata,
							},
							lipSync: studioFeedMap[mediaId]?.canvasSettings?.attributes?.lipSync && {
								...studioFeedMap[mediaId]?.canvasSettings?.attributes?.lipSync,
								metadata: generatedMedia[mediaId].metadata,
							},
							videoAnyone: studioFeedMap[mediaId]?.canvasSettings?.attributes?.videoAnyone && {
								...studioFeedMap[mediaId]?.canvasSettings?.attributes?.videoAnyone,
								metadata: generatedMedia[mediaId].metadata,
							},
						},
					},
				};
			}

			return null;
		});

		dispatch(
			setCanvasFeedMap(
				updateCanvasFeedItems(studioFeedDataMap, canvasId, 'media', studioFeedMapCopy),
			),
		);
		dispatch(updateCanvasPendingUpdateMediaList(Object.values(studioFeedMapCopy ?? {})));
	} else {
		dispatch(handelCanvasImagesErrorFromSocket(generationDetails));
	}
	dispatch(setBalance(credits));
};

export const moveStageToLatestGeneration = (value) => (dispatch) => {
	dispatch(setMoveStageToLatestGeneration(value));
};

export const setDefaultCanvasSettings = (userPreferences) => (dispatch, getState) => {
	if (userPreferences) {
		dispatch(
			setCanvasSettings({
				settings: userPreferences,
				fromLocalStorage: true,
			}),
		);
		dispatch(
			setCanvasSelectedModels({
				models: userPreferences.models,
				replace: true,
			}),
		);
		if (userPreferences.styles) {
			dispatch(
				setCanvasSelectedStyles({
					styles: userPreferences.styles,
					replace: true,
				}),
			);
		}
		dispatch(setSelectedLoras(userPreferences.attributes?.loras ?? []));

		if (userPreferences?.attributes?.characterId) {
			const allCharacters = listCharactersSelector(getState());
			const selectedCharacter = allCharacters.find(
				(value) => userPreferences?.attributes?.characterId === value?.id,
			);
			if (selectedCharacter) {
				dispatch(setSelectedCharacter(selectedCharacter));
			} else {
				dispatch(clearSelectedCharacter());
			}
		} else {
			dispatch(clearSelectedCharacter());
		}

		dispatch(handleReuseControlNetAndFacelock(userPreferences));
	}
};

export const saveImageSettingsForRemix = (image) => async (dispatch, getState) => {
	if (image.settings.attributes) {
		const { controlNet, facelock, character } = image.settings.attributes;
		const linkPrefix = 'https:/';
		const base64Pref = 'data:image/jpeg;base64,';

		if (controlNet && controlNet?.inputImage) {
			if (controlNet?.inputImage.substring(0, linkPrefix.length) === linkPrefix) {
				dispatch(uploadCanvasControlNetImageLinkAsync(controlNet?.inputImage));
			} else {
				dispatch(setCanvasControlNetImage(`${base64Pref}${controlNet?.inputImage}`));
			}
		}
		if (facelock && facelock?.inputImage) {
			if (facelock?.inputImage.substring(0, linkPrefix.length) === linkPrefix) {
				dispatch(uploadCanvasFaceLockImageLinkAsync(facelock?.inputImage));
			} else {
				dispatch(setCanvasFacelockImage(`${base64Pref}${facelock?.inputImage}`));
			}
		}
		if (character) {
			const allCharacters = listCharactersSelector(getState());
			const selectedCharacter = allCharacters.find((value) => character.id === value?.id);
			if (selectedCharacter) {
				dispatch(setSelectedCharacter(selectedCharacter));
			} else {
				dispatch(clearSelectedCharacter());
			}
		} else {
			dispatch(clearSelectedCharacter());
		}
	}

	dispatch(
		setCanvasSettings({
			settings: { ...image.settings, randomiseSeed: false },
		}),
	);

	if (image.model != null) {
		const lightningModels = Object.values(canvasLightningModelsSelector(getState()));
		const isLightningModel =
			lightningModels.length > 0 &&
			lightningModels.some((model) => model.id === image.model || model.name === image.model);
		const fluxModels = Object.values(canvasFluxModelsSelector(getState()));
		const isFluxModel =
			fluxModels.length > 0 &&
			fluxModels.some((model) => model.id === image.model || model.name === image.model);
		if (isLightningModel) {
			dispatch(setCanvasSelectedLightningModels([image.model]));
			dispatch(setCurrentGenerationStyleTab(GenerationStyleTabs.LIGHTNING_MODE));
		} else if (isFluxModel) {
			dispatch(setCanvasSelectedFluxModels([image.model]));
			dispatch(setCurrentGenerationStyleTab(GenerationStyleTabs.FLUX_MODE));
		} else {
			dispatch(
				setCanvasSelectedModels({
					models: [image.model],
					replace: true,
				}),
			);
		}
		dispatch(setCurrentGenerationStyleTab(GenerationStyleTabs.PRO_MODE));
	} else {
		dispatch(
			setCanvasSelectedModels({
				models: [],
				replace: true,
			}),
		);
		dispatch(setCanvasSelectedLightningModels([]));
		dispatch(setCanvasSelectedFluxModels([]));
	}

	if (image.style != null) {
		dispatch(
			setCanvasSelectedStyles({
				styles: [image.style],
				replace: true,
			}),
		);
		dispatch(setCurrentGenerationStyleTab(GenerationStyleTabs.MAGIC_MODE));
	} else {
		dispatch(
			setCanvasSelectedStyles({
				styles: [],
				replace: true,
			}),
		);
	}
};

export const handleCanvasImageClick = (media) => async (dispatch) => {
	if (media.thumbnail != null && media.thumbnail?.length) {
		dispatch(setSelectedCanvasMediaThumbnail({ thumbnail: media.thumbnail }));
	}
};
